"use client"

import { useState } from "react"
import UploadIcon from "../../../../assets/UploadIcon"
import SendInviteIcon from "../../../../assets/SendInviteIcon"
import { Switch } from "../../../Catalyst/switch"
import ErrorIcon from "../../../../assets/ErrorIcon"
import FileTypeIcon from "../../../../assets/FileTypeIcon"
import DeleteIcon from "../../../../assets/DeleteIcon"
import { uploadBulkUsers, checkUploadStatus } from "../../../../services/UsersService"
import { useToast } from "../../../../contexts/ToastContext"
import { AccessibleOrganization } from "../../../../store/slices/OrganizationsV3Slice"
import OrgSelectPopover from "../../../common/OrgSelectPopover"
import SingleSelectDropdown from "../../../common/SingleSelectDropdown"

interface InviteCsvSectionProps {
  // The target org is chosen here (ADM-92) rather than taken from the URL; the
  // dialog owns it because its title names the selected org.
  organizationId: number | null
  onOrganizationChange: (organizationId: number | null) => void
  organizationOptions: AccessibleOrganization[]
  isOrganizationOptionsLoading: boolean
  isOrganizationOptionsError: boolean
  roleOptions: { value: string; label: string }[]
  // Called once the upload job completes so the user list can refresh.
  onInvited?: () => void
}

const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`

// The server's error envelope varies by endpoint ({ msg } on newer ones,
// { message } / { error } / { details[] } on the bulk upload).
const getUploadErrorMessage = (data: any): string => {
  if (Array.isArray(data?.details) && data.details.length > 0) return data.details.join(", ")
  return data?.msg || data?.message || data?.error || "Failed to upload file. Please try again."
}

export function InviteCsvSection({
  organizationId,
  onOrganizationChange,
  organizationOptions,
  isOrganizationOptionsLoading,
  isOrganizationOptionsError,
  roleOptions,
  onInvited,
}: InviteCsvSectionProps) {
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true)
  const [role, setRole] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [validationError, setValidationError] = useState("")
  const [isValidated, setIsValidated] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [rowCount, setRowCount] = useState(0)

  const { showSuccessToast, showErrorToast } = useToast()

  // AC: submit stays disabled until a CSV is uploaded and an org and role are chosen.
  const canSubmit = isValidated && organizationId != null && role != null && !uploading

  const resetFile = () => {
    setFile(null)
    setValidationError("")
    setIsValidated(false)
    setProgress(0)
    setRowCount(0)
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    // Allow picking the same file again after removing it.
    e.target.value = ""
    if (selectedFile?.name.toLowerCase().endsWith(".csv")) {
      await handleValidatedFile(selectedFile)
    } else if (selectedFile) {
      setValidationError("Please upload a .csv file.")
    }
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const droppedFile = e.dataTransfer.files?.[0]
    if (droppedFile?.name.toLowerCase().endsWith(".csv")) {
      await handleValidatedFile(droppedFile)
    } else if (droppedFile) {
      setValidationError("Please upload a .csv file.")
    }
  }

  const handleValidatedFile = async (file: File) => {
    const result = await validateCSV(file)
    if (result.valid && result.rowCount) {
      setFile(file)
      setRowCount(result.rowCount)
      setValidationError("")
      setIsValidated(true)
    } else {
      setFile(null)
      setRowCount(0)
      setValidationError(result.error ?? "Invalid CSV format")
      setIsValidated(false)
    }
  }

  // Invites only add the users in the file: removeUsers=false, so existing
  // users missing from the CSV are never removed (unlike the original roster
  // sync on the User Management tab, which previews and confirms removals).
  const startUpload = async () => {
    if (!file || organizationId == null || !role) return
    try {
      setUploading(true)
      setProgress(0)
      const { job_id } = await uploadBulkUsers(file, organizationId, sendWelcomeEmail, false, role)
      pollUploadStatus(job_id)
    } catch (error: any) {
      setUploading(false)
      showErrorToast('Upload failed', getUploadErrorMessage(error?.response?.data));
    }
  }

  const pollUploadStatus = async (jobId: string) => {
    const maxPollingTime = 3 * 60 * 1000 // 3 minutes
    const pollingInterval = 500 // 500ms
    const startTime = Date.now()

    const poll = async () => {
      try {
        const { status, processed, totalRecords, failed_records } = await checkUploadStatus(jobId)

        // Calculate progress based on processed records
        if (totalRecords > 0) {
          setProgress(Math.min((processed / totalRecords) * 100, 100))
        }

        if (status === 'COMPLETED' || status === 'FAILED') {
          setUploading(false)

          if (status === 'COMPLETED') {
            const successfullyProcessed = processed - (failed_records || 0)

            if (successfullyProcessed === 0) {
              showErrorToast('Upload failed', 'There is an error processing your file. Please contact support.')
            } else {
              // Copy per the ADM-92 Figma ("You've sent (128) people an invite!").
              const title = sendWelcomeEmail
                ? `You've sent (${successfullyProcessed}) ${successfullyProcessed === 1 ? 'person' : 'people'} an invite!`
                : `You've added (${successfullyProcessed}) ${successfullyProcessed === 1 ? 'employee' : 'employees'}`
              let message = sendWelcomeEmail
                ? 'You can view the status of these new invites in your dashboard.'
                : 'No welcome emails were sent. You can view the new users in your dashboard and invite them anytime.'
              if (failed_records && failed_records > 0) {
                message += ` We couldn't process ${pluralize(failed_records, 'record', 'records')}.`
              }
              showSuccessToast(title, message)
              onInvited?.()
            }
          } else {
            showErrorToast('Upload failed', 'There is an error processing your file. Please contact support.')
          }

          resetFile()
          return
        }

        // Continue polling if not completed and within time limit
        if (Date.now() - startTime < maxPollingTime) {
          setTimeout(poll, pollingInterval)
        } else {
          setUploading(false)
          showErrorToast('Upload timeout', 'Upload is taking longer than expected. Please check the status later.')
        }
      } catch (error) {
        setUploading(false)
        showErrorToast('Status check failed', 'Failed to check upload status.')
      }
    }

    setTimeout(poll, pollingInterval)
  }

  const validateCSV = async (
    file: File
  ): Promise<{ valid: boolean; rowCount?: number; error?: string }> => {
    const expectedHeaders = ["first", "last", "email"]; // Header titles

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onerror = () => resolve({ valid: false, error: "The file could not be read." });
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const rows = text.split("\n").map((r) => r.trim()).filter(Boolean);

        if (rows.length < 2) {
          return resolve({ valid: false, error: "CSV must contain a header and at least one user row." });
        }

        // Validate header row
        const headers = rows[0].split(",").map(h => h.trim().toLowerCase());
        if (headers.length !== expectedHeaders.length ||
          !expectedHeaders.every((h, idx) => headers[idx] === h)) {
          return resolve({ valid: false, error: `Headers must be exactly: ${expectedHeaders.join(", ")}` });
        }

        // Validate each user row
        for (let i = 1; i < rows.length; i++) {
          const values = rows[i].split(",").map(v => v.trim());
          if (values.length < 3) {
            return resolve({ valid: false, error: `Row ${i + 1} must have 3 columns (first, last, email)` });
          }
          if (!values[0]) {
            return resolve({ valid: false, error: `Missing first name in row ${i + 1}` });
          }
          if (!values[1]) {
            return resolve({ valid: false, error: `Missing last name in row ${i + 1}` });
          }
          if (!values[2]) {
            return resolve({ valid: false, error: `Missing email in row ${i + 1}` });
          }
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(values[2])) {
            return resolve({ valid: false, error: `Invalid email format in row ${i + 1}: ${values[2]}` });
          }
        }
        resolve({ valid: true, rowCount: rows.length - 1 });
      };
      reader.readAsText(file);
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
      <h3 className="font-['Inter'] text-[14px] font-medium leading-[20px] text-[#525252] mb-1">Upload your CSV</h3>
      <p className="font-['Inter'] text-[12px] font-medium leading-[20px] text-[#525252] mb-3">
        Send invites to users in bulk. Your file must include column headers: 'first', 'last', and 'email'.
      </p>

      <button
        className={`w-full mb-4 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 p-10 transition ${isDragging ? "border-green-500 bg-green-50" : ""} ${validationError ? "border-red-500" : "border-gray-200 bg-white"}`}
        tabIndex={0}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            document.getElementById('csv-upload')?.click();
          }
        }}
      >
        {!file ? (
          <label htmlFor="csv-upload" className="flex cursor-pointer flex-col items-center">
            <div className="mb-4 shadow-light-sm p-2 border border-shadowborder rounded-lg">
              <UploadIcon />
            </div>
            <input id="csv-upload" type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
            <p className="mb-1 text8">
              <span className="text-green-500 desktext13">Click to upload</span> or drag and drop
            </p>
            <p className="text8 text-gray-500">CSV</p>
          </label>
        ) : (
          <div className="w-full">
            <div className="w-full flex justify-between items-center">
              <div className="flex items-center gap-3">
                <FileTypeIcon />
                <div className="flex flex-col items-start">
                  <div className="desktext15 text-textColor">{file.name}</div>
                  {/* "5 KB – 100% uploaded" per the Figma; the file is read and
                      validated in the browser before anything is sent. */}
                  <div className="desktext15 text-textColor3">
                    {(file.size / 1024).toFixed(1)} KB &ndash;{" "}
                    {uploading ? `Processing ${Math.round(progress)}%` : "100% uploaded"}
                  </div>
                </div>
              </div>
              <span
                role="button"
                tabIndex={uploading ? -1 : 0}
                className={`text-sm text-gray-500 ${uploading ? "cursor-not-allowed opacity-50" : "hover:text-red-500"}`}
                onClick={(e) => {
                  e.stopPropagation()
                  if (!uploading) resetFile()
                }}
                onKeyDown={(e) => {
                  if (!uploading && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault()
                    e.stopPropagation()
                    resetFile()
                  }
                }}
                aria-label="Remove uploaded file"
                aria-disabled={uploading}
              >
                <DeleteIcon />
              </span>
            </div>
            {uploading && (
              <div className="mb-4 w-full h-2 mt-5 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-green-500 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            )}
          </div>
        )}
      </button>

      {validationError && (
        <div className="mb-4 -mt-2 flex items-center justify-center text-sm text-red-600">
          <div className="mr-3"><ErrorIcon /></div>
          There was an issue uploading your CSV. {validationError}
        </div>
      )}

      <div className="mb-3 flex items-center justify-end gap-3">
        <OrgSelectPopover
          options={organizationOptions}
          value={organizationId}
          onChange={onOrganizationChange}
          isLoading={isOrganizationOptionsLoading}
          isError={isOrganizationOptionsError}
          disabled={uploading}
        />
        <SingleSelectDropdown label="Role" options={roleOptions} value={role} onChange={setRole} />
      </div>

      <div className="flex items-center justify-end gap-3">
        <div className="flex items-start gap-2">
          <Switch
            color="green"
            checked={sendWelcomeEmail}
            onChange={setSendWelcomeEmail}
            disabled={uploading}
            aria-label="Send welcome email for the CSV upload"
          />
          <span className="font-['Inter'] font-medium text-[14px] leading-[20px] text-[#344054]">Send welcome email</span>
        </div>
        <div className={`${!canSubmit ? 'opacity-50' : ''}`}>
          <button
            onClick={() => startUpload()}
            disabled={!canSubmit}
            title={!canSubmit && !uploading ? "Upload a CSV and choose an organization and role" : undefined}
            className="bg-white border border-neutral-200 rounded-lg shadow-sm px-3.5 py-2.5 flex items-center gap-1 hover:bg-gray-50 transition-colors disabled:cursor-not-allowed"
          >
            <SendInviteIcon />
            <span className="font-['Inter'] font-medium text-[14px] leading-[20px] text-neutral-900">
              {rowCount > 0 ? (
                uploading ? 'Uploading...' : `Add (${rowCount}) Employees${sendWelcomeEmail ? " and Send Emails" : ""}`
              ) : (
                "Send Bulk Invitation"
              )}
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}
